import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { Trophy, Timer, Users, Coins, TrendingUp, Bot } from 'lucide-react';
import { JackpotStatus } from '@/types';
import { api } from '@/services/api';
import { toast } from '@/store/useToast';

export function JackpotPage() {
  const { jackpot, user, fetchJackpot, placeBet, loading } = useStore();
  const [betAmount, setBetAmount] = useState('');
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [testBotAmounts, setTestBotAmounts] = useState<string[]>(['100', '500', '1000']);

  const isAdmin = user?.is_admin ?? false;

  useEffect(() => {
    fetchJackpot();
    const interval = setInterval(async () => {
      // Check if timer expired and trigger roll
      try {
        await api.checkJackpotTimer();
      } catch (error) {
        console.error('Failed to check timer:', error);
      }
      // Fetch updated jackpot state
      fetchJackpot();
    }, 3000); // Refresh every 3 seconds

    return () => clearInterval(interval);
  }, [fetchJackpot]);

  // Timer countdown
  useEffect(() => {
    if (!jackpot?.timer_end) {
      setTimeLeft(0);
      return;
    }

    const calculateTimeLeft = () => {
      const end = new Date(jackpot.timer_end!).getTime();
      const now = new Date().getTime();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      setTimeLeft(diff);
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [jackpot?.timer_end]);

  // Trigger spinning animation when status changes to ROLLING
  useEffect(() => {
    if (jackpot?.status === JackpotStatus.ROLLING && !spinning) {
      setSpinning(true);
      const baseRotation = 360 * 5;
      const randomAngle = Math.random() * 360;
      setRotation(baseRotation + randomAngle);

      setTimeout(() => {
        setSpinning(false);
        fetchJackpot(); // Refresh to show winner
      }, 4000);
    }
  }, [jackpot?.status]);

  const handlePlaceBet = async () => {
    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Введите корректную сумму ставки');
      return;
    }

    if (!user || amount > user.balance) {
      toast.error('Недостаточно средств');
      return;
    }

    try {
      await placeBet(amount);
      setBetAmount('');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Ошибка при размещении ставки');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddTestBots = async () => {
    try {
      const amounts = testBotAmounts.map(a => parseFloat(a)).filter(a => !isNaN(a) && a > 0);
      if (amounts.length === 0) {
        toast.error('Введите корректные суммы ставок');
        return;
      }

      await api.addTestBotsToJackpot(amounts);
      toast.success(`Добавлено ${amounts.length} тестовых ботов`);
      fetchJackpot();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Ошибка при добавлении ботов');
    }
  };

  const quickBetAmounts = [100, 500, 1000, 2000];

  const bets = jackpot?.bets || [];
  const totalPool = jackpot?.total_pool || 0;
  const winnerPrize = totalPool; // 100% to winner
  const platformCommission = 0; // Commission taken on withdrawal

  const isActive = jackpot?.status === JackpotStatus.ACTIVE;
  const isCompleted = jackpot?.status === JackpotStatus.COMPLETED;
  const winner = isCompleted && jackpot?.winner_id
    ? bets.find(bet => bet.user_id === jackpot.winner_id)
    : null;

  if (!jackpot) {
    return (
      <div className="min-h-screen bg-tg-theme-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-tg-theme-text">Загрузка джекпота...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6">
        <h1 className="text-3xl font-bold mb-2 text-center">🎰 Джекпот</h1>
        <p className="text-yellow-100 text-sm text-center">
          Делай ставку и выигрывай весь банк!
        </p>
      </div>

      {/* Prize Pool */}
      <div className="px-4 py-6">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-yellow-400 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-transparent"></div>
          <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-3 relative z-10" />
          <p className="text-purple-200 mb-2 relative z-10">Призовой фонд</p>
          <p className="text-5xl font-bold text-yellow-400 mb-2 relative z-10">
            {totalPool.toFixed(0)} ₽
          </p>
          <p className="text-sm text-purple-200 relative z-10">
            Победитель получит весь банк (100%)
          </p>
        </div>
      </div>

      {/* Timer */}
      {isActive && (
        <div className="px-4 mb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Timer className="w-5 h-5 text-yellow-400" />
              <p className="text-purple-200 text-sm">Время до розыгрыша</p>
            </div>
            {timeLeft > 0 ? (
              <>
                <p className={`text-4xl font-bold ${timeLeft <= 10 ? 'text-red-400 animate-pulse' : 'text-yellow-400'}`}>
                  {formatTime(timeLeft)}
                </p>
                <p className="text-xs text-purple-300 mt-2">
                  Таймер сбрасывается после каждой ставки
                </p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-purple-300 mt-2">⏳</p>
                <p className="text-sm text-purple-300 mt-2">
                  Ожидание второго участника
                </p>
                <p className="text-xs text-purple-400 mt-1">
                  Таймер запустится после второй ставки
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Wheel Animation */}
      {spinning && (
        <div className="px-4 mb-6">
          <div className="relative aspect-square max-w-sm mx-auto">
            {/* Outer glow effect */}
            <div className="absolute inset-0 rounded-full bg-yellow-400/30 blur-3xl animate-pulse"></div>

            {/* Spinning wheel */}
            <div className="relative w-full h-full">
              <div
                className="w-full h-full rounded-full border-8 border-yellow-400 shadow-2xl bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 flex items-center justify-center relative overflow-hidden"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)',
                }}
              >
                {/* Segments */}
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-full h-1 bg-white/20"
                    style={{
                      transformOrigin: 'center',
                      transform: `rotate(${i * 45}deg)`,
                    }}
                  />
                ))}

                {/* Center icon */}
                <div className="text-7xl z-10 drop-shadow-2xl">🎰</div>

                {/* Particles */}
                <div className="absolute inset-0">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                      style={{
                        top: `${50 + 40 * Math.sin((i * Math.PI * 2) / 12)}%`,
                        left: `${50 + 40 * Math.cos((i * Math.PI * 2) / 12)}%`,
                        animation: `float ${1 + Math.random()}s ease-in-out infinite`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Pointer arrow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 z-20">
                <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[30px] border-t-yellow-400 drop-shadow-lg"></div>
              </div>
            </div>
          </div>

          <p className="text-center text-yellow-400 font-bold text-2xl mt-6 animate-pulse">
            🎲 Определяем победителя...
          </p>
        </div>
      )}

      {/* Winner Announcement */}
      {isCompleted && winner && (
        <div className="px-4 mb-6">
          <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl p-6 text-center">
            <div className="animate-bounce mb-3">
              <Trophy className="w-16 h-16 text-purple-900 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-purple-900 mb-2">
              🎉 Победитель найден! 🎉
            </h2>
            <p className="text-purple-800 mb-2">
              {winner.username ? `@${winner.username}` : winner.first_name || `ID: ${winner.user_id}`}
            </p>
            <div className="bg-purple-900 text-yellow-400 rounded-xl p-4">
              <p className="text-sm mb-1">Выигрыш</p>
              <p className="text-3xl font-bold">
                {jackpot.winner_prize?.toFixed(0)} ₽
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bet Form */}
      {isActive && !spinning && (
        <div className="px-4 mb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <Coins className="w-6 h-6 mr-2 text-yellow-400" />
              Сделать ставку
            </h3>

            {/* Quick Bet Buttons */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {quickBetAmounts.map(amount => (
                <button
                  key={amount}
                  onClick={() => setBetAmount(amount.toString())}
                  className="bg-purple-600 hover:bg-purple-500 text-white py-2 px-3 rounded-lg font-semibold text-sm transition-colors"
                >
                  {amount}₽
                </button>
              ))}
            </div>

            {/* Custom Amount Input */}
            <div className="mb-4">
              <label className="block text-sm text-purple-200 mb-2">
                Или введите свою сумму:
              </label>
              <input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                placeholder="Введите сумму..."
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-purple-300 focus:outline-none focus:border-yellow-400"
                min="1"
              />
              {user && (
                <p className="text-xs text-purple-300 mt-2">
                  Ваш баланс: {user.balance.toFixed(2)} ₽
                </p>
              )}
            </div>

            {/* Place Bet Button */}
            <button
              onClick={handlePlaceBet}
              disabled={loading || !betAmount || parseFloat(betAmount) <= 0}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                loading || !betAmount || parseFloat(betAmount) <= 0
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-purple-900 hover:shadow-lg hover:scale-105 active:scale-95'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-900"></div>
                  <span>Обработка...</span>
                </span>
              ) : (
                '🎲 Сделать ставку'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Participants List */}
      <div className="px-4 mb-6">
        <h3 className="text-xl font-bold mb-4 flex items-center">
          <Users className="w-6 h-6 mr-2 text-yellow-400" />
          Участники ({bets.length})
        </h3>

        {bets.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 text-center">
            <p className="text-purple-200">
              Пока нет участников. Будьте первым!
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {bets
              .sort((a, b) => b.amount - a.amount)
              .map((bet, index) => (
                <div
                  key={bet.id}
                  className={`backdrop-blur-sm rounded-lg p-4 border ${
                    bet.user_id === user?.telegram_id
                      ? 'bg-yellow-400/20 border-yellow-400'
                      : 'bg-white/10 border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">
                          {bet.username ? `@${bet.username}` : bet.first_name || `ID: ${bet.user_id}`}
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-purple-200">
                          <TrendingUp className="w-3 h-3" />
                          <span>Шанс: {bet.chance.toFixed(2)}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-yellow-400 text-lg">
                        {bet.amount.toFixed(0)} ₽
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Admin Panel */}
      {isAdmin && (
        <div className="px-4 mb-6">
          <button
            onClick={() => setShowAdminPanel(!showAdminPanel)}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-semibold mb-4 flex items-center justify-center"
          >
            <Bot className="w-5 h-5 mr-2" />
            {showAdminPanel ? 'Скрыть админ-панель' : 'Показать админ-панель'}
          </button>

          {showAdminPanel && (
            <div className="bg-red-900/30 backdrop-blur-sm rounded-xl p-6 border border-red-500/50">
              <h3 className="text-xl font-bold mb-4 text-red-300">Админ-панель</h3>

              <div className="mb-4">
                <label className="block text-sm text-purple-200 mb-2">
                  Суммы ставок для тестовых ботов (по одной на бота):
                </label>
                <div className="space-y-2">
                  {testBotAmounts.map((amount, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => {
                          const newAmounts = [...testBotAmounts];
                          newAmounts[index] = e.target.value;
                          setTestBotAmounts(newAmounts);
                        }}
                        placeholder="Сумма ставки..."
                        className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-purple-300 focus:outline-none focus:border-red-400"
                        min="1"
                      />
                      <button
                        onClick={() => setTestBotAmounts(testBotAmounts.filter((_, i) => i !== index))}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setTestBotAmounts([...testBotAmounts, ''])}
                  className="mt-2 w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg text-sm"
                >
                  + Добавить еще бота
                </button>
              </div>

              <button
                onClick={handleAddTestBots}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-bold"
              >
                Добавить тестовых ботов ({testBotAmounts.length} шт)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Info Section */}
      <div className="px-4 pb-6">
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
          <h4 className="font-bold mb-2 text-yellow-400">Как играть?</h4>
          <ul className="text-sm text-purple-200 space-y-1">
            <li>• Сделайте ставку любой суммы</li>
            <li>• После каждой ставки таймер сбрасывается на 90 секунд</li>
            <li>• Ваш шанс на выигрыш = ваша ставка / общий банк</li>
            <li>• Когда таймер истечет, выбирается случайный победитель</li>
            <li>• Победитель получает весь банк (100%)</li>
            <li>• При выводе средств применяется комиссия 10%</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
